import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TopBar } from "../components/TopBar";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Mail, Sparkles, Building2, Clock, RefreshCw, XCircle, Eye, CheckCircle2, Ban } from "lucide-react";

const WINDOWS = [
  { key: null, label: "All threads" },
  { key: "last_7", label: "Last 7 days" },
  { key: "last_30", label: "Last 30 days" },
];

const STAGES = [
  "Reading message",
  "Separating promises from updates",
  "Checking existing commitments",
  "Looking for fulfilment evidence",
  "Preparing review",
];

const ANALYZE_TIMEOUT_MS = 60_000;

const newRequestId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function Gmail({ setPasteOpen, onAnalysis }) {
  const [threads, setThreads] = useState([]);
  const [connected, setConnected] = useState("");
  const [windowKey, setWindowKey] = useState(null);
  // Per-thread state machine: {state, requestId, controller, stage, result, startedAt, error}
  const [ts, setTs] = useState({});
  const tsRef = useRef({});
  tsRef.current = ts;
  const stageIntervalsRef = useRef({});
  const timeoutRef = useRef({});

  const setThreadState = useCallback((tid, patch) => {
    setTs((prev) => {
      const next = { ...prev, [tid]: { ...(prev[tid] || {}), ...patch } };
      tsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    api.gmailThreads(windowKey).then((res) => {
      setThreads(res.threads || []);
      setConnected(res.connected_email || "");
    });
  }, [windowKey]);

  // Cancel any in-flight requests on unmount so late responses cannot leak
  useEffect(() => {
    return () => {
      Object.values(tsRef.current).forEach((s) => {
        if (s?.controller) s.controller.abort();
      });
      Object.values(stageIntervalsRef.current).forEach((iv) => iv && clearInterval(iv));
      Object.values(timeoutRef.current).forEach((tm) => tm && clearTimeout(tm));
    };
  }, []);

  const startStageCycle = (tid) => {
    if (stageIntervalsRef.current[tid]) clearInterval(stageIntervalsRef.current[tid]);
    let i = 0;
    setThreadState(tid, { stage: STAGES[0] });
    stageIntervalsRef.current[tid] = setInterval(() => {
      i = Math.min(i + 1, STAGES.length - 1);
      setThreadState(tid, { stage: STAGES[i] });
    }, 1400);
  };

  const stopStageCycle = (tid) => {
    if (stageIntervalsRef.current[tid]) {
      clearInterval(stageIntervalsRef.current[tid]);
      stageIntervalsRef.current[tid] = null;
    }
  };

  const cancelAnalysis = (tid) => {
    const s = tsRef.current[tid];
    if (!s) return;
    if (s.controller) s.controller.abort();
    stopStageCycle(tid);
    if (timeoutRef.current[tid]) {
      clearTimeout(timeoutRef.current[tid]);
      timeoutRef.current[tid] = null;
    }
    setThreadState(tid, { state: "idle", requestId: null, controller: null, stage: null, error: null });
  };

  const analyzeThread = async (t) => {
    const tid = t.id;
    const current = tsRef.current[tid];
    // Idempotency: ignore if already analyzing or already awaiting review
    if (current?.state === "analyzing") return;
    if (current?.state === "awaiting_review" && current.result) {
      // Re-open review dialog for this thread
      onAnalysis?.(current.result);
      return;
    }
    const requestId = newRequestId();
    const controller = new AbortController();
    startStageCycle(tid);
    setThreadState(tid, {
      state: "analyzing",
      requestId,
      controller,
      startedAt: Date.now(),
      result: null,
      error: null,
    });
    // Timeout
    if (timeoutRef.current[tid]) clearTimeout(timeoutRef.current[tid]);
    timeoutRef.current[tid] = setTimeout(() => {
      const st = tsRef.current[tid];
      if (st?.state === "analyzing" && st.requestId === requestId) {
        controller.abort();
        stopStageCycle(tid);
        setThreadState(tid, { state: "failed", stage: null, error: "Timed out after 60 seconds." });
      }
    }, ANALYZE_TIMEOUT_MS);

    try {
      const res = await fetch(`${API}/gmail/analyze/${tid}?request_id=${encodeURIComponent(requestId)}`, {
        method: "POST",
        signal: controller.signal,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      // Strict validation of source thread + request id
      if (data.source_thread_id !== tid) {
        console.warn("[gmail] rejecting mismatched source_thread_id", { expected: tid, got: data.source_thread_id });
        stopStageCycle(tid);
        setThreadState(tid, { state: "failed", stage: null, error: "Response did not match this thread." });
        return;
      }
      if (data.request_id && data.request_id !== requestId) {
        console.warn("[gmail] stale response — request_id mismatch", { expected: requestId, got: data.request_id });
        return; // ignore silently, state stays analyzing (will be superseded)
      }
      const st = tsRef.current[tid];
      if (!st || st.requestId !== requestId) {
        // A newer request superseded this one, or state was reset
        console.warn("[gmail] stale response — state changed since request");
        return;
      }
      stopStageCycle(tid);
      if (timeoutRef.current[tid]) clearTimeout(timeoutRef.current[tid]);
      const n = (data.detected || []).length;
      const ff = (data.fulfillment_matches || []).length;
      const rc = (data.reconciliation_matches || []).length;
      const rej = (data.rejected || []).length;
      const dupes = (data.duplicate_candidates || []).length;
      if (n === 0 && ff === 0 && rc === 0 && rej === 0 && dupes === 0) {
        setThreadState(tid, { state: "idle", stage: null, result: null, requestId: null, controller: null });
        toast(`No new commitment detected in "${t.subject}".`);
        return;
      }
      setThreadState(tid, { state: "awaiting_review", stage: null, result: data, controller: null });
      const summary = rc > 0
        ? `${rc} lifecycle update${rc === 1 ? "" : "s"} to review`
        : ff > 0
          ? `Detected ${n} · ${ff} possible fulfilment — review required.`
          : `Detected ${n} commitment${n === 1 ? "" : "s"} — review required.`;
      toast.success(summary);
      onAnalysis?.({ ...data, __sourceThreadId: tid, __onReviewClose: () => {
        // If user closes without accepting, keep the awaiting_review state so they can re-open
      } });
    } catch (e) {
      stopStageCycle(tid);
      if (timeoutRef.current[tid]) clearTimeout(timeoutRef.current[tid]);
      if (e.name === "AbortError") {
        setThreadState(tid, { state: "idle", stage: null, error: null, requestId: null, controller: null });
        return;
      }
      const msg = e?.message || "Analysis failed";
      setThreadState(tid, { state: "failed", stage: null, error: String(msg), controller: null });
    }
  };

  const reopenReview = (tid) => {
    const s = tsRef.current[tid];
    if (s?.result) onAnalysis?.(s.result);
  };

  const buttonFor = (t) => {
    const s = ts[t.id] || { state: "idle" };
    if (s.state === "analyzing") {
      return (
        <div className="ml-2 shrink-0 flex items-center gap-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F] font-mono text-[11px]">
            <Sparkles className="h-3 w-3 animate-pulse" />
            {s.stage || "Reading…"}
          </div>
          <button
            onClick={() => cancelAnalysis(t.id)}
            data-testid={`gmail-cancel-${t.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ledger-border text-[11px] text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink"
          >
            <XCircle className="h-3 w-3" /> Cancel
          </button>
        </div>
      );
    }
    if (s.state === "awaiting_review") {
      return (
        <button
          onClick={() => reopenReview(t.id)}
          data-testid={`gmail-review-${t.id}`}
          className="ml-2 shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B45309] text-white text-xs font-medium hover:bg-[#92400E] transition-colors duration-200"
        >
          <Eye className="h-3.5 w-3.5" /> Review results
        </button>
      );
    }
    if (s.state === "failed") {
      return (
        <div className="ml-2 shrink-0 flex items-center gap-2">
          <span
            data-testid={`gmail-failed-${t.id}`}
            className="font-mono text-[11px] text-[#B91C1C]"
          >
            {s.error?.slice(0, 40) || "Failed"}
          </span>
          <button
            onClick={() => analyzeThread(t)}
            data-testid={`gmail-retry-${t.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ledger-border text-[11px] text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink"
          >
            <RefreshCw className="h-3 w-3" /> Retry
          </button>
        </div>
      );
    }
    if (s.state === "accepted") {
      return (
        <span
          data-testid={`gmail-accepted-${t.id}`}
          className="ml-2 shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D1FAE5] border border-[#10B981] text-[#047857] font-mono text-[11px]"
        >
          <CheckCircle2 className="h-3 w-3" /> Reviewed
        </span>
      );
    }
    if (s.state === "dismissed") {
      return (
        <span
          data-testid={`gmail-dismissed-${t.id}`}
          className="ml-2 shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F5F5F5] border border-ledger-border text-ledger-sub font-mono text-[11px]"
        >
          <Ban className="h-3 w-3" /> Dismissed
        </span>
      );
    }
    return (
      <button
        onClick={() => analyzeThread(t)}
        data-testid={`gmail-analyze-${t.id}`}
        className="ml-2 shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ledger-ink text-white text-xs font-medium hover:bg-neutral-800 transition-colors duration-200"
      >
        <Sparkles className="h-3.5 w-3.5" /> Detect
      </button>
    );
  };

  // Listen for accept/dismiss events scoped to a thread
  useEffect(() => {
    const acceptHandler = (e) => {
      const tid = e.detail?.sourceThreadId;
      if (!tid) return;
      setThreadState(tid, { state: "accepted", result: null, requestId: null });
    };
    const dismissHandler = (e) => {
      const tid = e.detail?.sourceThreadId;
      if (!tid) return;
      const s = tsRef.current[tid];
      // Only reset to dismissed if this thread was awaiting review
      if (s?.state === "awaiting_review") {
        setThreadState(tid, { state: "dismissed", result: null, requestId: null });
      }
    };
    window.addEventListener("cl:threadAccepted", acceptHandler);
    window.addEventListener("cl:reviewDismissed", dismissHandler);
    return () => {
      window.removeEventListener("cl:threadAccepted", acceptHandler);
      window.removeEventListener("cl:reviewDismissed", dismissHandler);
    };
  }, [setThreadState]);

  return (
    <div className="min-h-screen paper-grain" data-testid="gmail-page">
      <TopBar onOpenPaste={() => setPasteOpen(true)} />

      <main className="mx-auto max-w-[1700px] px-6 md:px-12 py-10 md:py-14">
        <div className="mb-8 flex items-start justify-between gap-6 flex-wrap animate-fadeUp">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">
              Connected source · Gmail (demo inbox)
            </div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl text-ledger-ink">
              Discover commitments in your inbox.
            </h1>
            <p className="mt-2 text-ledger-sub text-base max-w-2xl">
              This is a demo Gmail-style inbox. Pick a thread and review detected
              commitments before anything enters the ledger.
            </p>
            {connected && (
              <div className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] text-ledger-sub bg-white border border-ledger-border px-3 py-1 rounded-sm">
                <Mail className="h-3.5 w-3.5" /> {connected}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-1 flex-wrap">
              {WINDOWS.map((w) => (
                <button
                  key={w.label}
                  data-testid={`gmail-window-${w.key || "all"}`}
                  onClick={() => setWindowKey(w.key)}
                  className={
                    "px-3 py-1 font-mono text-[11px] uppercase tracking-wider rounded-sm border transition-colors duration-150 " +
                    (windowKey === w.key
                      ? "bg-ledger-ink text-white border-ledger-ink"
                      : "bg-white text-ledger-sub border-ledger-border hover:border-ledger-ink")
                  }
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border border-ledger-border bg-ledger-surface divide-y divide-ledger-border animate-fadeUp">
          {threads.length === 0 && (
            <div className="py-16 text-center text-sm text-ledger-muted font-mono">
              No threads in this window.
            </div>
          )}
          {threads.map((t) => (
            <div
              key={t.id}
              data-testid={`gmail-thread-${t.id}`}
              className="p-5 md:p-6 flex items-start gap-4 hover:bg-[#FFFDF6] transition-colors duration-150"
            >
              <div className="h-10 w-10 rounded-sm border border-ledger-border bg-white grid place-items-center font-mono text-xs shrink-0">
                {(t.from_name || "?")
                  .split(/\s+/).filter(Boolean).slice(0, 2)
                  .map((w) => w[0].toUpperCase()).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-sans text-sm font-semibold text-ledger-ink">
                    {t.from_name}
                  </span>
                  <span className="text-xs font-mono text-ledger-muted inline-flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> {t.from_org}
                  </span>
                  <span className="ml-auto text-[11px] font-mono text-ledger-muted inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {t.received_at_days_ago === 0 ? "today" : `${t.received_at_days_ago}d ago`}
                  </span>
                </div>
                <div className="mt-1 font-serif text-lg text-ledger-ink truncate">{t.subject}</div>
                <div className="mt-1 text-sm text-ledger-sub line-clamp-2">{t.snippet}</div>
              </div>
              {buttonFor(t)}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
